import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import './App.css';
import Navbar from './components/Navbar';
import Dashboard from './pages/Dashboard';
import History from './pages/History';
import Alerts from './pages/Alerts';
import Report from './pages/Report';
import Compare from './pages/Compare';

// ── Canvas animation ───────────────────────────────────────────────────────
const SHAPE_COUNT    = 20;
const PARTICLE_COUNT = 55;
const RING_COUNT     = 4;

// Lerp between dark-red and deep-purple in RGB
function accentRGB(t, phase) {
  // t = global time (int), phase = per-element offset
  const mix = (Math.sin(t * 0.00045 + phase) + 1) / 2;
  const r = Math.round(248 + (100 - 248) * mix);  // 248 → 100
  const g = Math.round( 50 + ( 20 -  50) * mix);  //  50 → 20
  const b = Math.round( 60 + (240 -  60) * mix);  //  60 → 240
  return [r, g, b];
}

function BackgroundFx() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let W = 0, H = 0;
    function resize() {
      W = canvas.width  = window.innerWidth;
      H = canvas.height = window.innerHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    // ── Wireframe geometric shapes ────────────────────────────────────────
    const shapes = Array.from({ length: SHAPE_COUNT }, (_, i) => ({
      x:        Math.random() * window.innerWidth,
      y:        Math.random() * window.innerHeight,
      vx:       (Math.random() - 0.5) * 0.22,
      vy:       (Math.random() - 0.5) * 0.22,
      size:     9 + Math.random() * 24,
      rot:      Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.005,
      type:     i % 3,           // 0 = triangle, 1 = diamond, 2 = hexagon
      phase:    Math.random() * Math.PI * 2,
      alpha:    0.09 + Math.random() * 0.14,
    }));

    // ── Floating particles ────────────────────────────────────────────────
    const particles = Array.from({ length: PARTICLE_COUNT }, () => ({
      x:     Math.random() * window.innerWidth,
      y:     Math.random() * window.innerHeight,
      speed: 0.35 + Math.random() * 0.75,
      size:  0.7 + Math.random() * 1.5,
      phase: Math.random() * Math.PI * 2,
      angle: -Math.PI * 0.55 + (Math.random() - 0.5) * 0.7,
    }));

    // ── Pulsing rings ─────────────────────────────────────────────────────
    const rings = Array.from({ length: RING_COUNT }, (_, i) => ({
      x:      0.2 + (i / RING_COUNT) * 0.75,   // x as fraction of W
      y:      0.15 + Math.random() * 0.7,
      maxR:   80 + Math.random() * 120,
      speed:  0.004 + Math.random() * 0.003,
      phase:  (i / RING_COUNT) * Math.PI * 2,
      colorP: Math.random() * Math.PI * 2,
    }));

    let raf, t = 0;

    function rgba([r, g, b], a) {
      return `rgba(${r},${g},${b},${a})`;
    }

    function drawShape(s) {
      ctx.save();
      ctx.translate(s.x, s.y);
      ctx.rotate(s.rot);
      ctx.beginPath();
      const n = s.type === 2 ? 6 : s.type === 0 ? 3 : 4;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + (s.type === 0 ? -Math.PI / 2 : s.type === 3 ? Math.PI / 4 : 0);
        const px = Math.cos(a) * s.size * (s.type === 1 && (i % 2 === 0) ? 1 : s.type === 1 ? 0.55 : 1);
        const py = Math.sin(a) * s.size * (s.type === 1 && (i % 2 === 0) ? 1 : s.type === 1 ? 0.55 : 1);
        i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
      }
      ctx.closePath();
      const [r, g, b] = accentRGB(t, s.phase);
      ctx.strokeStyle = `rgba(${r},${g},${b},${s.alpha})`;
      ctx.lineWidth = 0.9;
      ctx.stroke();
      ctx.restore();
    }

    function animate() {
      t++;
      ctx.clearRect(0, 0, W, H);

      // ── Pulsing rings ────────────────────────────────────────────────
      rings.forEach(ring => {
        const cx = ring.x * W;
        const cy = ring.y * H;
        // Draw 3 concentric expanding rings per anchor
        for (let k = 0; k < 3; k++) {
          const progress = ((t * ring.speed + ring.phase + k * 2.1) % (Math.PI * 2)) / (Math.PI * 2);
          const r = progress * ring.maxR;
          const alpha = (1 - progress) * 0.09;
          if (alpha < 0.005) continue;
          const [cr, cg, cb] = accentRGB(t, ring.colorP + k);
          ctx.strokeStyle = `rgba(${cr},${cg},${cb},${alpha})`;
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.stroke();
        }
      });

      // ── Geometric shapes ─────────────────────────────────────────────
      shapes.forEach(s => {
        s.x += s.vx; s.y += s.vy; s.rot += s.rotSpeed;
        if (s.x < -50) s.x = W + 50; else if (s.x > W + 50) s.x = -50;
        if (s.y < -50) s.y = H + 50; else if (s.y > H + 50) s.y = -50;
        drawShape(s);
      });

      // ── Connection lines between nearby shapes ───────────────────────
      for (let i = 0; i < shapes.length; i++) {
        for (let j = i + 1; j < shapes.length; j++) {
          const dx = shapes[i].x - shapes[j].x;
          const dy = shapes[i].y - shapes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 200) {
            const alpha = (1 - dist / 200) * 0.095;
            const [r, g, b] = accentRGB(t, (shapes[i].phase + shapes[j].phase) * 0.5);
            ctx.strokeStyle = `rgba(${r},${g},${b},${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(shapes[i].x, shapes[i].y);
            ctx.lineTo(shapes[j].x, shapes[j].y);
            ctx.stroke();
          }
        }
      }

      // ── Particles with short trail ───────────────────────────────────
      particles.forEach(p => {
        const dx = Math.cos(p.angle) * p.speed;
        const dy = Math.sin(p.angle) * p.speed;
        p.x += dx; p.y += dy;
        if (p.y < -10) { p.y = H + 10; p.x = Math.random() * W; }
        if (p.x > W + 10) { p.x = -10; p.y = Math.random() * H; }
        if (p.x < -10) { p.x = W + 10; p.y = Math.random() * H; }

        const [r, g, b] = accentRGB(t, p.phase);
        // Head
        ctx.fillStyle = `rgba(${r},${g},${b},0.75)`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        // Trail
        ctx.fillStyle = `rgba(${r},${g},${b},0.26)`;
        ctx.beginPath();
        ctx.arc(p.x - dx * 4, p.y - dy * 4, p.size * 0.6, 0, Math.PI * 2);
        ctx.fill();
      });

      raf = requestAnimationFrame(animate);
    }

    animate();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div className="bg-ambient" aria-hidden="true">
      <canvas ref={canvasRef} className="bg-canvas" />
      <div className="bg-blob bg-blob-1" />
      <div className="bg-blob bg-blob-2" />
      <div className="bg-blob bg-blob-3" />
      <div className="bg-scan" />
    </div>
  );
}

function App() {
  return (
    <Router>
      <BackgroundFx />
      <div className="app-layout">
        <Navbar />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/history" element={<History />} />
            <Route path="/alerts" element={<Alerts />} />
            <Route path="/compare" element={<Compare />} />
            <Route path="/report" element={<Report />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;
